/** Canonical question type keys used by the SurveyRenderer to decide
 *  which widget to mount. Backend question types historically use a mix
 *  of dashes, underscores, spaces, and casing — `normalizeQuestionType`
 *  collapses every known variant to one of these strings. */
export type CanonicalQuestionType =
  | "rating"
  | "rating-nps"
  | "rating-word"
  | "csat"
  | "radio"
  | "boolean"
  | "text"
  | "textarea"
  | "single-select"
  | "multi-select"
  | "matrix"
  | "ranking"
  | "country"
  | "file-upload"
  | "content-text"
  | "content-richtext"
  | "content-image"
  | "content-video";

const ALIAS_MAP: Record<string, CanonicalQuestionType> = {
  rating: "rating",
  "rating-nps": "rating-nps",
  "rating-word": "rating-word",
  "rating word": "rating-word",
  csat: "csat",
  radio: "radio",
  boolean: "boolean",
  text: "text",
  textarea: "textarea",
  "single-select": "single-select",
  single_select: "single-select",
  "single select": "single-select",
  "multi-select": "multi-select",
  multi_select: "multi-select",
  "multi select": "multi-select",
  matrix: "matrix",
  ranking: "ranking",
  country: "country",
  "file-upload": "file-upload",
  "content-text": "content-text",
  "content-richtext": "content-richtext",
  "content-image": "content-image",
  "content-video": "content-video",
};

/** Map any backend-provided `questionType` string to the canonical key
 *  the renderer switches on. Returns `null` for unknown types so callers
 *  can render a fallback. */
export function normalizeQuestionType(
  raw: string | null | undefined,
): CanonicalQuestionType | null {
  if (!raw) return null;
  const key = String(raw).toLowerCase();
  return ALIAS_MAP[key] ?? null;
}

const CONTENT_TYPES: ReadonlySet<CanonicalQuestionType> = new Set([
  "content-text",
  "content-richtext",
  "content-image",
  "content-video",
]);

export function isContentBlock(type: CanonicalQuestionType): boolean {
  return CONTENT_TYPES.has(type);
}
